/**
 * The card for someone who opens the sign-in page already signed in ("welcome back"), in the new design.
 *
 * What it does is src/hooks/useSignedInCard.ts and what it says is SIGNED_IN_WORDS, the same two today's card
 * (src/components/UserLoginCard.tsx) reads: where to continue (the satin gold action), the workspace they are in and
 * the others to switch to (the sealed switch), a pending invitation, and logging out.
 */
import React from 'react';
import { Avatar } from '../../kit/marks/Avatar';
import { Button } from '../../kit/button/Button';
import { SIGNED_IN_WORDS as W } from '../../../../components/auth/signInWords';
import { useSignedInCard } from '../../../../hooks/useSignedInCard';

export const SignedInCard: React.FC<{ redirectPath: string; titleLevel: 'h1' | 'h2'; titleId: string }> = ({
  redirectPath,
  titleLevel,
  titleId,
}) => {
  const card = useSignedInCard(redirectPath);
  const Title = titleLevel;
  if (!card.isAuthenticated) return null;
  return (
    <div className="pv-signin" id="login-section">
      <Title className="pv-signin-title" id={titleId}>
        {card.title}
      </Title>
      <div className="pv-signin-who">
        {card.photo ? <Avatar kind="photo" src={card.photo} name={card.name} size={36} /> : <Avatar kind="person" name={card.name} size={36} />}
        <p className="pv-signin-line">
          {W.loggedInWith}
          <strong>{card.email}</strong>
        </p>
      </div>
      {card.activeWorkspaceLabel && (
        <p className="pv-signin-line pv-signin-working">
          {W.workingIn}
          <strong>{card.activeWorkspaceLabel}</strong>
        </p>
      )}
      <Button variant="primary" onClick={card.continueOn}>
        {card.continueLabel}
      </Button>
      {card.showWorkspaces && (
        <div className="pv-signin-workspaces">
          <p className="pv-signin-ws-head">{W.workspaces}</p>
          {card.workspacesPending ? (
            <p className="pv-signin-line" role="status">{W.loadingWorkspaces}</p>
          ) : (
            <ul className="pv-signin-ws-list">
              {card.workspaceRows.map(({ ws, label, hint }) => (
                <li key={ws.accountId}>
                  <button
                    type="button"
                    className="pv-signin-ws"
                    data-active={ws.isActive ? 'true' : undefined}
                    disabled={card.switching}
                    aria-current={ws.isActive ? 'true' : undefined}
                    aria-label={ws.isActive ? W.currentWorkspace(label) : W.switchTo(label)}
                    onClick={() => card.switchWorkspace(ws)}
                  >
                    <span className="pv-signin-ws-dot" aria-hidden="true">{ws.isActive ? '●' : '○'}</span>
                    <span className="pv-signin-ws-name">{label}</span>
                    <span className="pv-signin-ws-hint">{hint}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {card.pendingInvite && (
            <button type="button" className="pv-signin-ws" aria-label={W.reviewInviteLabel} onClick={card.reviewInvite}>
              <span className="pv-signin-ws-dot" aria-hidden="true">✉</span>
              <span className="pv-signin-ws-name">{W.reviewInvite}</span>
            </button>
          )}
          {card.switchError && <p className="pv-signin-error" role="alert">{card.switchError}</p>}
        </div>
      )}
      <Button variant="secondary" onClick={card.logout}>
        {W.logout} <span className="pv-signin-btn-suffix">{W.logoutSuffix}</span>
      </Button>
    </div>
  );
};
